import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, fmtDate, API_BASE } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import StatusFiltro, { STATUS_FILTRO_DEFAULT, statusFiltroPassa } from "../../components/StatusFiltro";
import { toast } from "sonner";
import {
  Plus, Eye, Pencil, Power, PowerOff, Building2, Search, KeyRound, X, BarChart3,
  Sparkles, Check, Info, FileText, Upload, Download, Wallet, CalendarClock, ExternalLink, Mail,
  History, Hash, ShieldAlert,
} from "lucide-react";
import { onlyDigits, maskDocumento, docType, isValidCPF, isValidCNPJ, isValidDocumento } from "../../lib/documento";
import { passwordPolicyError, PASSWORD_HELP_TEXT } from "../../lib/passwordPolicy";
import AdminResetClientPasswordDialog from "../../components/AdminResetClientPasswordDialog";
import { MODELOS_OPERACAO, MODELO_OPERACIONAL, MODELO_TRADICIONAL, isOperacional, modeloMeta, modeloOperacaoDe } from "../../lib/modeloOperacao";

// Contrato Permanente + Régua de Inadimplência (item 9/17) — rótulos de
// apresentação dos tipos de aviso automático (D+1/D+5/D+9), mesmo código
// usado no backend (overdue_notifications.tipo), nunca inventado aqui.
const OVERDUE_TIPO_LABEL = { d1: "1º aviso", d5: "2º aviso", d9: "Aviso final" };
const OVERDUE_RESULTADO_LABEL = {
  enviado: "E-mail enviado", falha: "Falha no envio", sem_destinatario: "Sem destinatário cadastrado",
  pendente: "Pendente", reprocessando: "Reprocessando",
};

// Pacote 2, item 9 — base segura para o futuro Painel Admin Master. Consome
// GET /admin/dashboard-summary (require_role("admin_master") no backend) —
// rota NOVA, dedicada, que expõe só CONTAGENS estruturais por empresa (sem
// valor financeiro, sem conteúdo de ficha, sem nome de cliente). Deliberadamente
// NÃO reaproveita /dashboard (rota operacional do tenant) para não dar ao
// Admin Master acesso operacional aos dados internos das empresas como
// efeito colateral deste item. Indicadores completos (financeiro, horas,
// ranking por empresa) ficam para uma evolução futura e dedicada do Painel
// Admin Master — fora do escopo deste pacote.
function PainelAdminMaster({ companies }) {
  const [companyId, setCompanyId] = useState("");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/dashboard-summary", { params: { company_id: companyId } })
      .then(r => setSummary(r.data))
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  }, [companyId]);

  return (
    <Card className="p-5" data-testid="admin-painel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="font-heading font-semibold flex items-center gap-2"><BarChart3 size={18} className="text-blue-600" /> Painel — contagens da plataforma</div>
        <div className="w-56">
          <Select value={companyId || "todas"} onValueChange={v => setCompanyId(v === "todas" ? "" : v)}>
            <SelectTrigger data-testid="admin-painel-empresa"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas as empresas</SelectItem>
              {companies.map(c => <SelectItem key={c.id} value={c.id}>{c.razao_social}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {loading ? (
        <div className="text-sm text-slate-400">Carregando...</div>
      ) : summary && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ["Empresas", summary.companies_count],
              ["Fichas", summary.totals.tickets_count],
              ["Clientes", summary.totals.clients_count],
              ["Usuários", summary.totals.users_count],
            ].map(([label, value]) => (
              <div key={label} className="p-3 rounded-md bg-slate-50 border border-slate-200" data-testid={`admin-painel-stat-${label.toLowerCase()}`}>
                <div className="text-xs text-slate-500">{label}</div>
                <div className="font-heading text-xl font-bold text-slate-900">{value}</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-3">
            Contagens estruturais — sem valores financeiros nem conteúdo operacional das empresas. Um painel financeiro/operacional completo fica para uma evolução futura e dedicada do Painel Admin Master.
          </p>
        </>
      )}
    </Card>
  );
}

// Mesma ordem fixa de frontend/src/pages/admin/Planos.jsx (item 1 do
// ajuste de UX) — nunca depende da ordem do Mongo/preço/data de criação.
const PLAN_ORDER = { basic: 0, gold: 1, personalizado: 2 };
const sortPlans = (list) => [...list].sort((a, b) => (PLAN_ORDER[a.codigo] ?? 99) - (PLAN_ORDER[b.codigo] ?? 99));

const emptyCompany = {
  razao_social: "", nome_fantasia: "", cnpj: "", telefone: "", email: "", logo_url: "",
  // `endereco` é o campo legado (texto livre) — não tem mais input próprio no
  // formulário desde o ajuste pós-homologação, mas segue carregado/enviado
  // sem alteração para não descartar dado já existente de empresas antigas.
  endereco: "",
  cep: "", logradouro: "", numero: "", complemento: "", bairro: "", municipio: "", uf: "",
  require_approval: false, responsavel_nome: "", responsavel_email: "", contacts: [""],
  situacao_cadastral: "", cnae_principal_codigo: "", cnae_principal_descricao: "",
  cnaes_secundarios: [], natureza_juridica: "",
  data_adesao: "",
  // NEXUS — Modelo de operação do tenant. Empresa nova nasce tradicional
  // (compatibilidade); o modelo operacional é sempre escolha consciente.
  modelo_operacao: MODELO_TRADICIONAL,
};
const emptyAdmin = { name: "", email: "", password: "" };
const emptyAccessEmail = { new_email: "" };
const emptyPrimaryAdmin = { name: "", email: "", password: "" };

// Tela do Admin Master: cadastro das EMPRESAS CLIENTES da plataforma LOOTUS.
// Não é a mesma coisa que Configurações → Empresa (que é "configurações da
// MINHA empresa", para quem já pertence a um tenant) — por isso usa rotas
// próprias (/admin/companies), nunca /config/company.
export default function Empresas() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Item 4 — filtro Ativos/Inativos/Todos, padrão Ativos. Empresas usa
  // `status` (string "active"/outro), diferente do `active` booleano das
  // demais telas — statusFiltroPassa recebe o booleano já traduzido aqui.
  const [companiesStatusFiltro, setCompaniesStatusFiltro] = useState(STATUS_FILTRO_DEFAULT);
  const filteredItems = useMemo(
    () => items.filter(c => statusFiltroPassa(companiesStatusFiltro, c.status === "active")),
    [items, companiesStatusFiltro],
  );
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // empresa sendo editada, ou null = criando
  const [form, setForm] = useState(emptyCompany);
  const [adminForm, setAdminForm] = useState(emptyAdmin);
  const [viewing, setViewing] = useState(null); // detalhe (somente leitura)
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [pwCompany, setPwCompany] = useState(null); // empresa alvo da redefinição de senha
  // Pacote prioritário — e-mail de acesso (login do administrador
  // principal) é um campo DIFERENTE do e-mail comercial/financeiro da
  // empresa — nunca confundir os dois formulários/estados.
  const [accessEmailCompany, setAccessEmailCompany] = useState(null);
  const [accessEmailForm, setAccessEmailForm] = useState(emptyAccessEmail);
  // Correções de homologação (P3) — o e-mail de acesso precisa aparecer
  // DENTRO do formulário Editar empresa (não só na tela de detalhe
  // somente-leitura, onde já existia desde a rodada anterior).
  // undefined = ainda carregando; null = empresa sem admin principal;
  // objeto = admin principal carregado.
  const [editPrimaryAdmin, setEditPrimaryAdmin] = useState(undefined);
  const [primaryAdminCompany, setPrimaryAdminCompany] = useState(null);
  const [primaryAdminForm, setPrimaryAdminForm] = useState(emptyPrimaryAdmin);
  // Planos/Recursos (Consolidação Comercial) — plano LOOTUS contratado
  // (Básico/Gold/Personalizado — única fonte de verdade de plano; o campo
  // legado "Plano" Basico/Plus/Pro foi removido), recursos efetivos (plano +
  // overrides) e contrato comercial da empresa em edição/consulta.
  const [plans, setPlans] = useState([]);
  const [resourcesFor, setResourcesFor] = useState(null); // empresa cujo popover de recursos está aberto
  const [resourcesData, setResourcesData] = useState(null);
  const [editResources, setEditResources] = useState(null);
  const [editContract, setEditContract] = useState(null);
  const [contractForm, setContractForm] = useState({ data_assinatura: "", observacao: "" });
  const [contractUploading, setContractUploading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  // Gestão Comercial SaaS — resumo (plano/mensalidade/vencimento/cobrança/
  // contrato) só para LEITURA aqui (item 13); a condição comercial completa
  // (desconto, reajuste, suspensão, cobranças) é gerenciada em Gestão
  // Comercial, para não duplicar um formulário grande dentro deste modal.
  const [editCommercial, setEditCommercial] = useState(null);
  // Contrato Permanente + Régua de Inadimplência (item 17) — histórico de
  // notificações de UMA empresa, buscado sob demanda ao abrir o diálogo
  // (nunca junto do resumo comercial, que já é carregado sempre).
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Causa raiz do bug "Gold mostrando seletor individual de recurso" —
  // openEdit() nunca limpava editResources/editCommercial/editContract/
  // editPrimaryAdmin ANTES de disparar as buscas assíncronas da empresa
  // nova; se o modal fosse fechado pelo X/clique fora (onOpenChange, não
  // pelo botão "Cancelar" — o único ponto que resetava esses estados) e
  // reaberto para OUTRA empresa, os dados da empresa ANTERIOR (inclusive
  // `plan.codigo === "personalizado"`) continuavam na tela até a resposta
  // nova chegar — e, em caso de respostas fora de ordem (rede lenta),
  // podiam nem chegar a ser sobrescritos corretamente. editTargetIdRef
  // guarda qual empresa é a "atual" de verdade; cada callback assíncrono
  // só aplica setState se a resposta ainda corresponde a ela — resposta
  // de uma empresa que já não é mais a exibida é descartada. Correção de
  // lógica de estado, não CSS/ocultação visual.
  const editTargetIdRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, plansRes] = await Promise.all([
        api.get("/admin/companies"),
        api.get("/admin/plans").catch(() => ({ data: [] })),
      ]);
      setItems(data);
      setPlans(plansRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const planName = (id) => plans.find(p => p.id === id)?.nome || null;

  // Cada load* abaixo só aplica o resultado (sucesso OU erro) se `cid`
  // ainda for a empresa correntemente sendo editada (editTargetIdRef) —
  // se o operador já fechou/trocou de empresa antes da resposta chegar,
  // o resultado é descartado em vez de sobrescrever a tela com dado da
  // empresa errada.
  const loadEditResources = async (cid) => {
    try {
      const { data } = await api.get(`/admin/companies/${cid}/features`);
      if (editTargetIdRef.current === cid) setEditResources(data);
    } catch {
      if (editTargetIdRef.current === cid) setEditResources(null);
    }
  };
  const loadEditContract = async (cid) => {
    try {
      const { data } = await api.get(`/admin/companies/${cid}/contract`);
      if (editTargetIdRef.current !== cid) return;
      setEditContract(data);
      setContractForm({
        data_assinatura: data.contrato_data_assinatura || "",
        observacao: data.contrato_observacao || "",
      });
    } catch {
      if (editTargetIdRef.current === cid) setEditContract(null);
    }
  };
  const loadEditCommercial = async (cid) => {
    try {
      const { data } = await api.get(`/admin/companies/${cid}/commercial`);
      if (editTargetIdRef.current === cid) setEditCommercial(data);
    } catch {
      if (editTargetIdRef.current === cid) setEditCommercial(null);
    }
  };
  // Contrato Permanente + Régua de Inadimplência (item 17) — histórico de
  // notificações sob demanda; company_id sempre do path (nunca do payload).
  const openHistory = async (cid) => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/admin/companies/${cid}/notificacoes-inadimplencia`);
      setHistoryItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setHistoryLoading(false); }
  };
  // Correções de homologação (P3) — mesma fonte de verdade já usada pela
  // tela de detalhe (GET /admin/companies/{id} já devolve primary_admin,
  // decriptado, a partir de company.primary_admin_user_id → users.email).
  // Nunca uma cópia/campo novo — só reaproveita o que já existe.
  const loadEditPrimaryAdmin = async (cid) => {
    try {
      const { data } = await api.get(`/admin/companies/${cid}`);
      if (editTargetIdRef.current === cid) setEditPrimaryAdmin(data.primary_admin || null);
    } catch {
      if (editTargetIdRef.current === cid) setEditPrimaryAdmin(null);
    }
  };

  const openResources = async (c) => {
    setResourcesFor(c);
    try { const { data } = await api.get(`/admin/companies/${c.id}/features`); setResourcesData(data); }
    catch (e) { toast.error(formatApiError(e)); setResourcesData(null); }
  };

  const changePlan = async (planoId) => {
    if (!editing) return;
    try {
      await api.put(`/admin/companies/${editing.id}/plan`, { plano_id: planoId });
      toast.success("Plano atualizado");
      loadEditResources(editing.id);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const changeOverride = async (codigo, override) => {
    if (!editing) return;
    try {
      await api.put(`/admin/companies/${editing.id}/features/${codigo}`, { override });
      toast.success("Recurso atualizado");
      loadEditResources(editing.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveContractInfo = async () => {
    if (!editing) return;
    try {
      await api.put(`/admin/companies/${editing.id}/contract`, contractForm);
      toast.success("Contrato atualizado");
      loadEditContract(editing.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onContractFile = async (file) => {
    if (!file || !editing) return;
    if (file.type !== "application/pdf") return toast.error("Envie um arquivo PDF");
    setContractUploading(true);
    try {
      const file_data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await api.post(`/admin/companies/${editing.id}/contract/upload`, { file_name: file.name, file_data });
      toast.success("Contrato enviado");
      loadEditContract(editing.id);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setContractUploading(false); }
  };

  const onLogoFile = async (file) => {
    if (!file || !editing) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) return toast.error("Envie um arquivo PNG, JPEG ou WEBP");
    setLogoUploading(true);
    try {
      const file_data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await api.post(`/admin/companies/${editing.id}/logo`, { file_name: file.name, file_data });
      setForm(f => ({ ...f, logo_url: file_data }));
      toast.success("Logo enviada");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLogoUploading(false); }
  };

  const dlContract = () => {
    if (!editing) return;
    const url = `${API_BASE}/admin/companies/${editing.id}/contract/download`;
    const token = localStorage.getItem("token");
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" })
      .then(r => r.blob()).then(b => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b); a.download = editContract?.contrato_arquivo_nome || "contrato.pdf"; a.click();
      }).catch(() => toast.error("Falha ao baixar"));
  };

  const openNew = () => {
    editTargetIdRef.current = null; // nenhuma empresa "atual" — nova, sem id ainda
    setForm(emptyCompany); setAdminForm(emptyAdmin); setEditing(null);
    setEditResources(null); setEditContract(null); setEditCommercial(null);
    setEditPrimaryAdmin(undefined); setOpen(true);
  };
  const openEdit = (c) => {
    // Limpa ANTES de disparar as buscas — nunca deixa a tela mostrar, nem
    // por um instante, dado de uma empresa diferente da que está sendo
    // aberta agora (ver comentário de editTargetIdRef acima).
    editTargetIdRef.current = c.id;
    setEditResources(null); setEditContract(null); setEditCommercial(null);
    setEditPrimaryAdmin(undefined);
    setForm({
      ...emptyCompany, ...c,
      contacts: (c.contacts && c.contacts.length ? c.contacts : [""]),
      cnaes_secundarios: c.cnaes_secundarios || [],
    });
    setEditing(c); setOpen(true);
    loadEditResources(c.id);
    loadEditContract(c.id);
    loadEditCommercial(c.id);
    loadEditPrimaryAdmin(c.id);
  };

  const openView = async (c) => {
    editTargetIdRef.current = c.id; // mesmo guard — loadEditCommercial é compartilhada com a edição
    setEditCommercial(null);
    try {
      const { data } = await api.get(`/admin/companies/${c.id}`);
      setViewing(data);
      // Vencimento/plano exibidos aqui precisam vir da MESMA condição
      // comercial vigente que "Resumo comercial" (edição) e Gestão
      // Comercial já usam — nunca do campo bruto data_vencimento (legado,
      // só preenchido manualmente no passado, quase sempre vazio).
      loadEditCommercial(c.id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const buscarCnpj = async () => {
    const clean = onlyDigits(form.cnpj);
    if (docType(form.cnpj) !== "cnpj") return; // botão só aparece para CNPJ; defesa extra
    setCnpjLoading(true);
    try {
      const { data } = await api.get(`/admin/companies/cnpj-lookup/${clean}`);
      setForm(f => {
        // Telefones retornados entram em `contacts`, sem duplicar quem já
        // estava lá (comparação simples por texto igual após trim).
        const existingContacts = f.contacts.filter(c => c.trim());
        const mergedContacts = [...existingContacts];
        for (const p of (data.contacts || [])) {
          if (p && !mergedContacts.some(m => m.trim() === p.trim())) mergedContacts.push(p);
        }
        // CNAEs secundários: mescla por código, sem duplicar.
        const existingCnaes = f.cnaes_secundarios || [];
        const mergedCnaes = [...existingCnaes];
        for (const s of (data.cnaes_secundarios || [])) {
          if (s.codigo && !mergedCnaes.some(m => m.codigo === s.codigo)) mergedCnaes.push(s);
        }
        return {
          ...f,
          razao_social: data.razao_social || f.razao_social,
          nome_fantasia: data.nome_fantasia || f.nome_fantasia,
          situacao_cadastral: data.situacao_cadastral || f.situacao_cadastral,
          cnae_principal_codigo: data.cnae_principal_codigo || f.cnae_principal_codigo,
          cnae_principal_descricao: data.cnae_principal_descricao || f.cnae_principal_descricao,
          cnaes_secundarios: mergedCnaes,
          natureza_juridica: data.natureza_juridica || f.natureza_juridica,
          cep: data.cep || f.cep,
          logradouro: data.logradouro || f.logradouro,
          numero: data.numero || f.numero,
          complemento: data.complemento || f.complemento,
          bairro: data.bairro || f.bairro,
          municipio: data.municipio || f.municipio,
          uf: data.uf || f.uf,
          endereco: data.endereco || f.endereco,
          telefone: data.telefone || f.telefone,
          email: data.email || f.email,
          contacts: mergedContacts.length ? mergedContacts : [""],
        };
      });
      toast.success("Dados do CNPJ preenchidos — revise antes de salvar");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCnpjLoading(false); }
  };

  // Pacote consolidado pós-homologação, Prioridade 2 — consulta automática
  // de CEP: ao completar 8 dígitos válidos, preenche logradouro/bairro/
  // município/UF via o backend (nunca depende exclusivamente do
  // frontend ir direto à internet — o backend também valida e serve de
  // fonte de verdade). Número é sempre manual (nunca preenchido aqui);
  // complemento continua editável. Só preenche campos ainda VAZIOS —
  // nunca apaga algo que o usuário já digitou.
  const buscarCep = async (rawCep) => {
    const clean = onlyDigits(rawCep);
    if (clean.length !== 8) return;
    setCepLoading(true);
    try {
      const { data } = await api.get(`/admin/companies/cep-lookup/${clean}`);
      setForm(f => ({
        ...f,
        logradouro: f.logradouro.trim() ? f.logradouro : (data.logradouro || ""),
        bairro: f.bairro.trim() ? f.bairro : (data.bairro || ""),
        municipio: f.municipio.trim() ? f.municipio : (data.municipio || ""),
        uf: f.uf.trim() ? f.uf : (data.uf || ""),
        complemento: f.complemento.trim() ? f.complemento : (data.complemento || ""),
      }));
    } catch (e) {
      toast.error(e?.response?.status === 404 ? "CEP não encontrado" : formatApiError(e));
    } finally {
      setCepLoading(false);
    }
  };

  const setContact = (i, value) => {
    const next = [...form.contacts]; next[i] = value; setForm({ ...form, contacts: next });
  };
  const addContact = () => setForm({ ...form, contacts: [...form.contacts, ""] });
  const removeContact = (i) => setForm({ ...form, contacts: form.contacts.filter((_, idx) => idx !== i) });

  const setCnae = (i, field, value) => {
    const next = form.cnaes_secundarios.map((s, idx) => idx === i ? { ...s, [field]: value } : s);
    setForm({ ...form, cnaes_secundarios: next });
  };
  const addCnae = () => setForm({ ...form, cnaes_secundarios: [...form.cnaes_secundarios, { codigo: "", descricao: "" }] });
  const removeCnae = (i) => setForm({ ...form, cnaes_secundarios: form.cnaes_secundarios.filter((_, idx) => idx !== i) });

  // Campos obrigatórios (ajuste final do Bloco B, item 5) — validados aqui
  // no frontend E também no backend (admin_create_company/admin_update_company
  // rejeitam com 400 se algo faltar); o frontend nunca é a única barreira.
  const validateRequired = () => {
    const faltando = [];
    if (!form.razao_social.trim()) faltando.push("Razão Social");
    if (!form.nome_fantasia.trim()) faltando.push("Nome / Nome Fantasia");
    if (!form.email.trim()) faltando.push("E-mail");
    if (!form.responsavel_nome.trim()) faltando.push("Responsável");
    if (!form.responsavel_email.trim()) faltando.push("E-mail do responsável");
    if (!form.contacts.some(c => c.trim())) faltando.push("ao menos um telefone/celular/contato");
    if (!form.cnpj.trim()) faltando.push("CPF ou CNPJ");
    if (faltando.length) {
      toast.error(`Campos obrigatórios não preenchidos: ${faltando.join(", ")}`);
      return false;
    }
    if (!isValidDocumento(form.cnpj)) {
      toast.error("CPF ou CNPJ inválido — verifique os dígitos informados");
      return false;
    }
    return true;
  };

  const submit = async () => {
    if (!validateRequired()) return;
    const payload = {
      ...form,
      contacts: form.contacts.map(c => c.trim()).filter(Boolean),
      cnaes_secundarios: form.cnaes_secundarios.filter(s => s.codigo.trim() || s.descricao.trim()),
    };
    try {
      if (editing) {
        await api.put(`/admin/companies/${editing.id}`, payload);
        toast.success("Empresa atualizada");
      } else {
        if (!adminForm.name.trim() || !adminForm.email.trim() || !adminForm.password.trim()) {
          return toast.error("Informe nome, e-mail e senha do administrador inicial");
        }
        const adminPwErr = passwordPolicyError(adminForm.password);
        if (adminPwErr) return toast.error(adminPwErr);
        const { data: created } = await api.post("/admin/companies", { ...payload, admin: adminForm });
        // Item 3 — o número do contrato (atribuído pelo backend, nunca
        // escolhido aqui) aparece imediatamente após o cadastro.
        const contratoTxt = created?.contract_number ? ` — Contrato nº ${String(created.contract_number).padStart(6, "0")}` : "";
        toast.success(`Empresa e administrador inicial criados${contratoTxt}`);
      }
      setOpen(false); setForm(emptyCompany); setAdminForm(emptyAdmin); setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleStatus = async (c) => {
    const action = c.status === "active" ? "deactivate" : "activate";
    try {
      await api.post(`/admin/companies/${c.id}/${action}`);
      toast.success(action === "activate" ? "Empresa reativada" : "Empresa inativada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // E-mail de ACESSO (login do administrador principal) — NUNCA o e-mail
  // comercial/financeiro da empresa (esse continua editado só pelo
  // formulário normal de cadastro). Reaproveita a mesma empresa "viewing"
  // já aberta para refletir o valor novo sem precisar reabrir o modal.
  const submitAccessEmail = async () => {
    if (!accessEmailForm.new_email.trim()) return toast.error("Informe o novo e-mail de acesso");
    try {
      const { data } = await api.put(`/admin/companies/${accessEmailCompany.id}/access-email`, accessEmailForm);
      toast.success("E-mail de acesso atualizado");
      setAccessEmailCompany(null); setAccessEmailForm(emptyAccessEmail);
      if (viewing && viewing.id === accessEmailCompany.id) {
        setViewing({ ...viewing, primary_admin: data.primary_admin });
      }
      if (editing && editing.id === accessEmailCompany.id) {
        setEditPrimaryAdmin(data.primary_admin);
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Correções de homologação, item 20 — empresa sem administrador principal
  // definido: cria o admin reaproveitando a MESMA validação/estrutura já
  // usada por admin_create_company (nunca uma segunda implementação de
  // "criar usuário admin"), nunca associa silenciosamente um usuário
  // qualquer já existente.
  const submitCreatePrimaryAdmin = async () => {
    if (!primaryAdminForm.name.trim() || !primaryAdminForm.email.trim() || !primaryAdminForm.password.trim()) {
      return toast.error("Preencha nome, e-mail e senha do administrador principal");
    }
    const primaryAdminPwErr = passwordPolicyError(primaryAdminForm.password);
    if (primaryAdminPwErr) return toast.error(primaryAdminPwErr);
    try {
      const { data } = await api.post(`/admin/companies/${primaryAdminCompany.id}/primary-admin`, primaryAdminForm);
      toast.success("Administrador principal definido");
      setPrimaryAdminCompany(null); setPrimaryAdminForm(emptyPrimaryAdmin);
      if (editing && editing.id === primaryAdminCompany.id) setEditPrimaryAdmin(data.primary_admin);
      if (viewing && viewing.id === primaryAdminCompany.id) setViewing({ ...viewing, primary_admin: data.primary_admin });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const enderecoLinha = (c) => {
    const partes = [c.logradouro, c.numero, c.complemento, c.bairro].filter(Boolean).join(", ");
    const cidadeUf = [c.municipio, c.uf].filter(Boolean).join("/");
    return [partes, cidadeUf].filter(Boolean).join(" — ");
  };

  const tipoDoc = docType(form.cnpj);
  const docPreenchido = onlyDigits(form.cnpj).length > 0;
  const docInvalido = docPreenchido && tipoDoc !== null && !isValidDocumento(form.cnpj);
  const docIncompleto = docPreenchido && tipoDoc === null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold flex items-center gap-2"><Building2 size={28} /> Empresas</h1>
          <p className="text-slate-500 text-sm">Empresas clientes da plataforma LOOTUS</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusFiltro value={companiesStatusFiltro} onChange={setCompaniesStatusFiltro} testId="empresas-filtro-status" label="Situação" />
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={openNew} data-testid="empresa-nova">
            <Plus size={16} /> Nova empresa
          </Button>
        </div>
      </div>

      <PainelAdminMaster companies={items} />

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left">
              <tr>
                <th className="py-3 px-4">Empresa</th><th>CPF/CNPJ</th><th>Responsável</th>
                <th>Plano LOOTUS</th>
                <th>Adesão</th><th>Vencimento</th><th>Situação</th><th>Administrador</th>
                <th className="text-right pr-4">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map(c => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="py-3 px-4">
                    <div className="font-medium text-slate-900">{c.razao_social}</div>
                    {c.nome_fantasia && <div className="text-xs text-slate-500">{c.nome_fantasia}</div>}
                    {isOperacional(c) && (
                      <Badge className={`${modeloMeta(MODELO_OPERACIONAL).badge} mt-1 text-[10px]`} data-testid={`empresa-modelo-${c.id}`}>
                        Operacional
                      </Badge>
                    )}
                  </td>
                  <td>{c.cnpj || "-"}</td>
                  <td>{c.responsavel_nome || "-"}</td>
                  <td>
                    {c.plano_id ? (
                      <button type="button" onClick={() => openResources(c)} className="inline-flex items-center gap-1 hover:opacity-80" data-testid={`empresa-plano-lootus-${c.id}`}>
                        <Badge className="bg-blue-100 text-blue-800">{planName(c.plano_id) || "-"}</Badge>
                        <Info size={14} className="text-slate-400" />
                      </button>
                    ) : <span className="text-slate-400 text-xs">Sem plano</span>}
                  </td>
                  <td className="text-slate-500">{c.data_adesao ? fmtDate(c.data_adesao) : "-"}</td>
                  <td className="text-slate-500">{c.data_vencimento ? fmtDate(c.data_vencimento) : "-"}</td>
                  <td><Badge className={c.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}>{c.status === "active" ? "Ativa" : "Inativa"}</Badge></td>
                  <td>{c.primary_admin_name || c.primary_email || "-"}</td>
                  <td className="text-right pr-4 space-x-1 whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => openView(c)} title="Visualizar" data-testid={`empresa-ver-${c.id}`}><Eye size={16} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(c)} title="Editar" data-testid={`empresa-editar-${c.id}`}><Pencil size={16} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setPwCompany(c)} title="Redefinir senha do administrador" data-testid={`empresa-redefinir-senha-${c.id}`}><KeyRound size={16} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => toggleStatus(c)} title={c.status === "active" ? "Inativar empresa" : "Ativar empresa"} data-testid={`empresa-toggle-${c.id}`}>
                      {c.status === "active" ? <PowerOff size={16} /> : <Power size={16} />}
                    </Button>
                  </td>
                </tr>
              ))}
              {!loading && filteredItems.length === 0 && (
                <tr><td colSpan={9} className="py-8 text-center text-slate-400">{items.length === 0 ? "Nenhuma empresa cadastrada" : "Nenhuma empresa para este filtro"}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Criar / Editar — layout largo em até 3 colunas para reduzir rolagem vertical */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl">
          <DialogHeader><DialogTitle>{editing ? "Editar empresa" : "Nova empresa"}</DialogTitle></DialogHeader>
          <div className="max-h-[75vh] overflow-y-auto pr-1 space-y-5">

            {/* Identificação */}
            <section>
              <div className="font-heading font-semibold text-slate-900 mb-2 pb-1 border-b border-slate-200">Identificação</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <div className="lg:col-span-3">
                  <Label>CPF ou CNPJ *</Label>
                  <div className="flex gap-2">
                    <Input
                      data-testid="empresa-form-cnpj" value={form.cnpj}
                      onChange={e => setForm({ ...form, cnpj: maskDocumento(e.target.value) })}
                      placeholder="CPF (000.000.000-00) ou CNPJ (00.000.000/0001-00)"
                      className={docInvalido ? "border-red-400" : ""}
                    />
                    {tipoDoc === "cnpj" && isValidCNPJ(form.cnpj) && (
                      <Button type="button" variant="outline" onClick={buscarCnpj} disabled={cnpjLoading} data-testid="empresa-form-buscar-cnpj">
                        <Search size={16} /> {cnpjLoading ? "Buscando..." : "Buscar dados"}
                      </Button>
                    )}
                  </div>
                  {docPreenchido && (
                    <p className={`text-xs mt-1 ${docInvalido || docIncompleto ? "text-red-500" : "text-emerald-600"}`}>
                      {tipoDoc === "cpf" && (isValidCPF(form.cnpj) ? "CPF válido — consulta automática não se aplica a pessoa física" : "CPF inválido")}
                      {tipoDoc === "cnpj" && (isValidCNPJ(form.cnpj) ? "CNPJ válido" : "CNPJ inválido")}
                      {docIncompleto && "Documento incompleto — informe um CPF (11 dígitos) ou CNPJ (14 dígitos)"}
                    </p>
                  )}
                </div>
                <div className="lg:col-span-2"><Label>Razão Social *</Label><Input data-testid="empresa-form-razao" value={form.razao_social} onChange={e => setForm({ ...form, razao_social: e.target.value })} /></div>
                <div><Label>Nome Fantasia *</Label><Input data-testid="empresa-form-fantasia" value={form.nome_fantasia} onChange={e => setForm({ ...form, nome_fantasia: e.target.value })} /></div>
                <div><Label>Telefone</Label><Input value={form.telefone} onChange={e => setForm({ ...form, telefone: e.target.value })} /></div>
                <div>
                  <Label>E-mail comercial da empresa *</Label>
                  <Input data-testid="empresa-form-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                  <p className="text-[11px] text-slate-400 mt-0.5">Diferente do e-mail do responsável e do e-mail de acesso do administrador (abaixo) — nunca o mesmo campo.</p>
                </div>
                <div>
                  <Label>Logo</Label>
                  <Input placeholder="URL da imagem (ou envie um arquivo abaixo)" value={form.logo_url?.startsWith("data:") ? "" : form.logo_url} onChange={e => setForm({ ...form, logo_url: e.target.value })} disabled={form.logo_url?.startsWith("data:")} />
                  {editing ? (
                    <label className="flex items-center gap-2 text-xs mt-1 border border-dashed border-slate-300 rounded-md px-2 py-1.5 cursor-pointer hover:bg-slate-50 w-fit">
                      <Upload size={14} /> {logoUploading ? "Enviando..." : (form.logo_url?.startsWith("data:") ? "Trocar arquivo" : "Enviar arquivo (PNG/JPEG/WEBP)")}
                      <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="empresa-form-logo-arquivo"
                        onChange={e => onLogoFile(e.target.files?.[0])} disabled={logoUploading} />
                    </label>
                  ) : (
                    <p className="text-[11px] text-slate-400 mt-0.5">Upload de arquivo disponível depois de salvar a empresa (edição).</p>
                  )}
                  {form.logo_url?.startsWith("data:") && (
                    <button type="button" className="text-[11px] text-blue-600 hover:underline mt-0.5" onClick={() => setForm(f => ({ ...f, logo_url: "" }))}>Remover e usar URL em vez de arquivo</button>
                  )}
                </div>
              </div>

              {tipoDoc === "cnpj" && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <div className="text-sm font-medium text-slate-700 mb-1">Dados do CNPJ</div>
                  <p className="text-xs text-slate-500 mb-2">Preenchidos pela consulta automática — continuam editáveis antes de salvar.</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    <div><Label>Situação cadastral</Label><Input data-testid="empresa-form-situacao" value={form.situacao_cadastral} onChange={e => setForm({ ...form, situacao_cadastral: e.target.value })} /></div>
                    <div><Label>Natureza jurídica</Label><Input data-testid="empresa-form-natureza" value={form.natureza_juridica} onChange={e => setForm({ ...form, natureza_juridica: e.target.value })} /></div>
                    <div><Label>CNAE principal (código)</Label><Input data-testid="empresa-form-cnae-codigo" value={form.cnae_principal_codigo} onChange={e => setForm({ ...form, cnae_principal_codigo: e.target.value })} /></div>
                    <div className="lg:col-span-3"><Label>CNAE principal (descrição)</Label><Input data-testid="empresa-form-cnae-descricao" value={form.cnae_principal_descricao} onChange={e => setForm({ ...form, cnae_principal_descricao: e.target.value })} /></div>
                    <div className="lg:col-span-3">
                      <Label>Atividades econômicas secundárias</Label>
                      <div className="space-y-2 mt-1">
                        {form.cnaes_secundarios.map((s, i) => (
                          <div key={i} className="flex gap-2">
                            <Input className="w-32" data-testid={`empresa-form-cnae-sec-codigo-${i}`} value={s.codigo} onChange={e => setCnae(i, "codigo", e.target.value)} placeholder="Código" />
                            <Input data-testid={`empresa-form-cnae-sec-descricao-${i}`} value={s.descricao} onChange={e => setCnae(i, "descricao", e.target.value)} placeholder="Descrição" />
                            <Button type="button" size="sm" variant="ghost" onClick={() => removeCnae(i)} title="Remover"><X size={16} /></Button>
                          </div>
                        ))}
                      </div>
                      <Button type="button" size="sm" variant="outline" className="mt-2" onClick={addCnae} data-testid="empresa-form-add-cnae-sec">
                        <Plus size={14} /> Adicionar CNAE secundário
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* Endereço */}
            <section>
              <div className="font-heading font-semibold text-slate-900 mb-2 pb-1 border-b border-slate-200">Endereço</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <div>
                  <Label>CEP{cepLoading && <span className="text-xs text-slate-400 font-normal ml-1">buscando endereço...</span>}</Label>
                  <Input data-testid="empresa-form-cep" value={form.cep}
                    onChange={e => setForm({ ...form, cep: e.target.value })}
                    onBlur={e => buscarCep(e.target.value)} placeholder="00000-000" />
                </div>
                <div className="lg:col-span-2"><Label>Logradouro</Label><Input data-testid="empresa-form-logradouro" value={form.logradouro} onChange={e => setForm({ ...form, logradouro: e.target.value })} /></div>
                <div><Label>Número</Label><Input data-testid="empresa-form-numero" value={form.numero} onChange={e => setForm({ ...form, numero: e.target.value })} /></div>
                <div><Label>Complemento</Label><Input data-testid="empresa-form-complemento" value={form.complemento} onChange={e => setForm({ ...form, complemento: e.target.value })} /></div>
                <div><Label>Bairro</Label><Input data-testid="empresa-form-bairro" value={form.bairro} onChange={e => setForm({ ...form, bairro: e.target.value })} /></div>
                <div className="lg:col-span-2"><Label>Município</Label><Input data-testid="empresa-form-municipio" value={form.municipio} onChange={e => setForm({ ...form, municipio: e.target.value })} /></div>
                <div><Label>UF</Label><Input data-testid="empresa-form-uf" value={form.uf} onChange={e => setForm({ ...form, uf: e.target.value.toUpperCase() })} maxLength={2} /></div>
              </div>
            </section>

            {/* Responsável e Contatos */}
            <section>
              <div className="font-heading font-semibold text-slate-900 mb-2 pb-1 border-b border-slate-200">Responsável e Contatos</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <div><Label>Nome do responsável *</Label><Input data-testid="empresa-form-responsavel-nome" value={form.responsavel_nome} onChange={e => setForm({ ...form, responsavel_nome: e.target.value })} /></div>
                <div className="lg:col-span-2"><Label>E-mail do responsável *</Label><Input type="email" data-testid="empresa-form-responsavel-email" value={form.responsavel_email} onChange={e => setForm({ ...form, responsavel_email: e.target.value })} /></div>
                <div className="lg:col-span-3">
                  <Label>Contatos (telefones/celulares) — pelo menos 1 obrigatório *</Label>
                  <div className="space-y-2 mt-1">
                    {form.contacts.map((c, i) => (
                      <div key={i} className="flex gap-2">
                        <Input data-testid={`empresa-form-contato-${i}`} value={c} onChange={e => setContact(i, e.target.value)} placeholder="(21) 9 9999-0000" />
                        {form.contacts.length > 1 && (
                          <Button type="button" size="sm" variant="ghost" onClick={() => removeContact(i)} title="Remover contato"><X size={16} /></Button>
                        )}
                      </div>
                    ))}
                  </div>
                  <Button type="button" size="sm" variant="outline" className="mt-2" onClick={addContact} data-testid="empresa-form-add-contato">
                    <Plus size={14} /> Adicionar contato
                  </Button>
                </div>
              </div>
            </section>

            {/* Correções de homologação (P3) — "Acesso ao LOOTUS" é um
                conceito DIFERENTE de "Responsável e Contatos" acima e do
                "E-mail" oficial da empresa: é o e-mail de LOGIN do
                administrador principal (users.email via
                primary_admin_user_id), nunca um campo novo/paralelo. Só
                existe para uma empresa já criada (editing) — na criação, o
                admin principal é definido pelo formulário de cadastro
                inicial mais abaixo. */}
            {editing && (
              <section>
                <div className="font-heading font-semibold text-slate-900 mb-2 pb-1 border-b border-slate-200 flex items-center gap-2">
                  <Mail size={16} /> Acesso ao LOOTUS
                </div>
                {editPrimaryAdmin === undefined && <div className="text-sm text-slate-400">Carregando...</div>}
                {editPrimaryAdmin && (
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <Label>E-mail de acesso *</Label>
                      <div className="font-medium text-slate-900" data-testid="empresa-form-email-acesso">{editPrimaryAdmin.email}</div>
                      <div className="text-[11px] text-slate-400 mt-1">E-mail utilizado pelo administrador principal para entrar no LOOTUS e recuperar a senha.</div>
                    </div>
                    <Button type="button" size="sm" variant="outline"
                      onClick={() => { setAccessEmailCompany({ id: editing.id, primary_admin: editPrimaryAdmin }); setAccessEmailForm(emptyAccessEmail); }}
                      data-testid="empresa-form-editar-email-acesso">
                      <Pencil size={14} /> Alterar
                    </Button>
                  </div>
                )}
                {editPrimaryAdmin === null && (
                  <div className="space-y-2">
                    <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="empresa-form-sem-admin-principal">
                      Administrador principal não configurado. Sem um administrador principal, ninguém consegue entrar no LOOTUS por esta empresa.
                    </div>
                    <Button type="button" size="sm" variant="outline"
                      onClick={() => { setPrimaryAdminCompany(editing); setPrimaryAdminForm(emptyPrimaryAdmin); }}
                      data-testid="empresa-form-criar-admin-principal">
                      <Plus size={14} /> Definir administrador principal
                    </Button>
                  </div>
                )}
              </section>
            )}

            {/* Administração — o Plano LOOTUS (Básico/Gold/Personalizado) não
                é definido aqui: toda empresa nova nasce em Básico
                automaticamente (ver admin_create_company) e é ajustado
                depois na seção "Plano LOOTUS e Recursos" abaixo (edição) —
                nunca um segundo campo de plano paralelo neste formulário. */}
            <section>
              <div className="font-heading font-semibold text-slate-900 mb-2 pb-1 border-b border-slate-200">Administração</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                <div><Label>Data de adesão</Label><Input type="date" data-testid="empresa-form-adesao" value={form.data_adesao || ""} onChange={e => setForm({ ...form, data_adesao: e.target.value })} /></div>
                <div className="lg:col-span-2">
                  <Label>Modelo de operação</Label>
                  <Select value={modeloOperacaoDe(form)} onValueChange={v => setForm({ ...form, modelo_operacao: v })}>
                    <SelectTrigger data-testid="empresa-form-modelo-operacao"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MODELOS_OPERACAO.map(m => (
                        <SelectItem key={m.valor} value={m.valor} data-testid={`empresa-form-modelo-${m.valor}`}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">{modeloMeta(modeloOperacaoDe(form)).descricao}</p>
                </div>
                <div className="flex items-end">
                  <p className="text-xs text-slate-500">
                    O vencimento da assinatura não é informado aqui — vem da condição comercial vigente (Gestão Comercial).
                  </p>
                </div>

                <div className="lg:col-span-3 p-3 rounded-md bg-slate-50 border border-slate-200">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" checked={!!form.require_approval} onChange={e => setForm({ ...form, require_approval: e.target.checked })} className="mt-1" />
                    <div>
                      <div className="font-medium text-slate-900">Exigir aprovação do supervisor</div>
                      <div className="text-xs text-slate-500">Mesma configuração disponível em Configurações → Empresa para o admin da própria empresa.</div>
                    </div>
                  </label>
                </div>

                {!editing && (
                  <>
                    <div className="lg:col-span-3 pt-2 border-t border-slate-100">
                      <div className="text-sm font-medium text-slate-700 mb-1">Administrador inicial</div>
                      <p className="text-xs text-slate-500 mb-2">Cria a empresa e o primeiro administrador dela em uma única operação.</p>
                    </div>
                    <div><Label>Nome *</Label><Input data-testid="empresa-admin-name" value={adminForm.name} onChange={e => setAdminForm({ ...adminForm, name: e.target.value })} /></div>
                    <div><Label>E-mail *</Label><Input type="email" data-testid="empresa-admin-email" value={adminForm.email} onChange={e => setAdminForm({ ...adminForm, email: e.target.value })} /></div>
                    <div>
                      <Label>Senha inicial *</Label>
                      <Input type="password" data-testid="empresa-admin-password" value={adminForm.password} onChange={e => setAdminForm({ ...adminForm, password: e.target.value })} />
                      <p className="text-xs text-slate-400 mt-1">{PASSWORD_HELP_TEXT}</p>
                    </div>
                  </>
                )}
              </div>
            </section>

            {/* Planos/Recursos (Fundação Comercial, itens 5/6) — plano
                contratado é uma ação exclusiva do Admin Master, com efeito
                imediato (rota dedicada, fora do "Salvar" geral do
                formulário); só disponível ao editar uma empresa já
                existente (uma empresa nova já nasce em Básico
                automaticamente, ver backend). */}
            {editing && (
              <section>
                <div className="font-heading font-semibold text-slate-900 mb-2 pb-1 border-b border-slate-200 flex items-center gap-2">
                  <Sparkles size={16} className="text-primary" /> Plano LOOTUS e Recursos
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label>Plano contratado</Label>
                    <Select value={editResources?.plan?.id || ""} onValueChange={changePlan}>
                      <SelectTrigger data-testid="empresa-form-plano-lootus"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                      <SelectContent>
                        {sortPlans(plans).map(p => <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">Alteração aplicada imediatamente.</p>
                  </div>
                </div>

                {/* Ajuste de UX — item 8: seleção individual de recurso só
                    faz sentido (e só é mostrada) para Personalizado. Em
                    Básico/Gold o próprio plano já define os recursos
                    integralmente — mostrar os mesmos 6 selects ali era
                    redundante ("Incluído conforme o plano" em todos) e dava
                    a falsa impressão de que dava para ajustar recurso a
                    recurso mesmo fora do Personalizado. */}
                {editResources?.plan?.codigo === "personalizado" ? (
                  <div className="mt-3">
                    <div className="text-sm font-medium text-slate-700 mb-1">Recursos contratados</div>
                    <p className="text-xs text-slate-500 mb-2">
                      Plano Personalizado — selecione individualmente os recursos oficiais contratados por esta empresa.
                    </p>
                    <div className="space-y-2">
                      {(editResources?.resources || []).map(r => {
                        const overrideValue = r.origin === "override_enabled" ? "enabled" : r.origin === "override_disabled" ? "disabled" : "inherit";
                        return (
                          <div key={r.codigo} className="flex items-center justify-between gap-3 p-2 rounded-md border border-slate-200">
                            <div className="text-sm">
                              <div className="font-medium text-slate-900">{r.nome}</div>
                              <div className="text-xs text-slate-500">{r.available ? "Contratado" : "Não contratado"} para esta empresa</div>
                            </div>
                            <div className="w-48">
                              <Select value={overrideValue} onValueChange={v => changeOverride(r.codigo, v)}>
                                <SelectTrigger data-testid={`empresa-override-${r.codigo}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="inherit">Não contratado</SelectItem>
                                  <SelectItem value="enabled">Contratado</SelectItem>
                                  <SelectItem value="disabled">Explicitamente removido</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        );
                      })}
                      {!(editResources?.resources || []).length && <p className="text-xs text-slate-400">Nenhum recurso cadastrado ainda.</p>}
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 mt-3">
                    Recursos incluídos automaticamente pelo plano {editResources?.plan?.nome || "contratado"} —
                    sem seleção individual (disponível apenas para o plano Personalizado).
                  </p>
                )}
              </section>
            )}

            {/* Gestão Comercial SaaS (item 13) — resumo comercial só de
                LEITURA aqui: plano, mensalidade efetiva, próximo vencimento,
                situação da cobrança e contrato. Edição completa da condição
                comercial (desconto, reajuste, suspensão, cobranças) fica em
                Gestão Comercial, para não duplicar um formulário grande
                dentro deste modal de cadastro. */}
            {editing && editCommercial && (
              <section>
                <div className="font-heading font-semibold text-slate-900 mb-2 pb-1 border-b border-slate-200 flex items-center gap-2">
                  <Wallet size={16} className="text-primary" /> Resumo comercial
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                    <div className="text-xs text-slate-500">Plano</div>
                    <div className="font-heading font-semibold text-slate-900">{editCommercial.plano?.nome || "Sem plano"}</div>
                  </div>
                  <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                    <div className="text-xs text-slate-500">Mensalidade</div>
                    <div className="font-heading font-semibold text-slate-900">
                      {editCommercial.plano ? `R$ ${editCommercial.valor_mensal_efetivo.toFixed(2)}` : "-"}
                    </div>
                    {editCommercial.desconto_valor > 0 && (
                      <div className="text-[11px] text-emerald-700">desconto de R$ {editCommercial.desconto_valor.toFixed(2)} aplicado</div>
                    )}
                  </div>
                  <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                    <div className="text-xs text-slate-500">Data de adesão</div>
                    <div className="font-heading font-semibold text-slate-900">{editCommercial.data_adesao ? fmtDate(editCommercial.data_adesao) : "-"}</div>
                  </div>
                  <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                    {/* Item 1/40 — nunca um campo para "escolher" o dia; é
                        sempre derivado da data de adesão acima. */}
                    <div className="text-xs text-slate-500">Vencimento mensal</div>
                    <div className="font-heading font-semibold text-slate-900">{editCommercial.vencimento_mensal_dia ? `Todo dia ${editCommercial.vencimento_mensal_dia}` : "-"}</div>
                  </div>
                  <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                    <div className="text-xs text-slate-500">Próxima emissão</div>
                    <div className="font-heading font-semibold text-slate-900">
                      {editCommercial.term.cobranca_ativa ? (editCommercial.proxima_emissao ? fmtDate(editCommercial.proxima_emissao) : "-") : "-"}
                    </div>
                  </div>
                  <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                    <div className="text-xs text-slate-500">Próximo vencimento</div>
                    <div className="font-heading font-semibold text-slate-900">
                      {editCommercial.term.cobranca_ativa ? (editCommercial.proximo_vencimento ? fmtDate(editCommercial.proximo_vencimento) : "-") : "-"}
                    </div>
                  </div>
                  <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                    <div className="text-xs text-slate-500">Cobrança</div>
                    <Badge className={editCommercial.term.cobranca_ativa ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>
                      {editCommercial.term.cobranca_ativa ? "Ativa" : "Suspensa"}
                    </Badge>
                  </div>
                  {/* Contrato Permanente + Régua de Inadimplência (item 17)
                      — número do contrato (nunca editável aqui) e o estado
                      financeiro/etapa, mesma fonte que o painel global de
                      Gestão Comercial (backend: _company_delinquency_status). */}
                  <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                    <div className="text-xs text-slate-500">Contrato nº</div>
                    <div className="font-heading font-semibold text-slate-900 flex items-center gap-1">
                      <Hash size={14} className="text-slate-400" /> {editCommercial.contract_number_display}
                    </div>
                  </div>
                  <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                    <div className="text-xs text-slate-500">Status financeiro</div>
                    <Badge className={editCommercial.inadimplencia.financially_blocked ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"}>
                      {editCommercial.inadimplencia.financially_blocked ? "Bloqueado" : "Normal"}
                    </Badge>
                  </div>
                  <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                    <div className="text-xs text-slate-500">Dias em atraso</div>
                    <div className="font-heading font-semibold text-slate-900">{editCommercial.inadimplencia.dias_atraso || 0}</div>
                  </div>
                  <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                    <div className="text-xs text-slate-500">Etapa de inadimplência</div>
                    <div className="font-heading font-semibold text-slate-900 text-sm">{editCommercial.inadimplencia.etapa_label || "Em dia"}</div>
                  </div>
                  <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                    <div className="text-xs text-slate-500">Última notificação</div>
                    <div className="font-heading font-semibold text-slate-900 text-sm">
                      {editCommercial.inadimplencia.ultima_notificacao
                        ? `${OVERDUE_TIPO_LABEL[editCommercial.inadimplencia.ultima_notificacao.tipo] || editCommercial.inadimplencia.ultima_notificacao.tipo} — ${fmtDate(editCommercial.inadimplencia.ultima_notificacao.sent_at)}`
                        : "-"}
                    </div>
                  </div>
                  <div className="p-3 rounded-md bg-slate-50 border border-slate-200 md:col-span-2">
                    <div className="text-xs text-slate-500">Próxima etapa</div>
                    <div className="font-heading font-semibold text-slate-900 text-sm">{editCommercial.inadimplencia.proxima_acao || "Nenhuma ação necessária"}</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <Button type="button" variant="outline" size="sm"
                    onClick={() => navigate(`/admin/comercial?empresa=${editing.id}`)} data-testid="empresa-ir-gestao-comercial">
                    <ExternalLink size={14} /> Gerenciar condição comercial em Gestão Comercial
                  </Button>
                  <Button type="button" variant="outline" size="sm"
                    onClick={() => openHistory(editing.id)} data-testid="empresa-ver-historico-notificacoes">
                    <History size={14} /> Histórico de notificações
                  </Button>
                </div>
              </section>
            )}

            {/* Gestão Comercial SaaS (item 11) — contrato comercial de
                assinatura da empresa CLIENTE com a LOOTUS (não confundir com
                futuros contratos/franquias operacionais dos clientes finais
                de cada tenant). Sem status artificial: o PDF assinado é a
                própria evidência. */}
            {editing && (
              <section>
                <div className="font-heading font-semibold text-slate-900 mb-2 pb-1 border-b border-slate-200 flex items-center gap-2">
                  <FileText size={16} className="text-primary" /> Contrato comercial LOOTUS
                </div>
                {editContract?.has_file && editContract.contrato_data_assinatura && (
                  <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 mb-3" data-testid="empresa-contrato-resumo">
                    Contrato assinado em {fmtDate(editContract.contrato_data_assinatura)}
                  </p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label>Data da assinatura</Label>
                    <Input type="date" value={contractForm.data_assinatura || ""} onChange={e => setContractForm({ ...contractForm, data_assinatura: e.target.value })} data-testid="empresa-contrato-data" />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Observação</Label>
                    <Textarea value={contractForm.observacao || ""} onChange={e => setContractForm({ ...contractForm, observacao: e.target.value })} data-testid="empresa-contrato-observacao" />
                  </div>
                </div>
                <div className="mt-2">
                  <Button type="button" variant="outline" onClick={saveContractInfo} data-testid="empresa-contrato-salvar-status">Salvar</Button>
                </div>

                <div className="mt-4 pt-3 border-t border-slate-100">
                  <div className="text-sm font-medium text-slate-700 mb-2">Arquivo do contrato assinado</div>
                  {editContract?.has_file ? (
                    <div className="flex items-center justify-between flex-wrap gap-2 p-3 rounded-md bg-slate-50 border border-slate-200">
                      <div className="text-sm">
                        <div className="font-medium text-slate-900">{editContract.contrato_arquivo_nome}</div>
                        <div className="text-xs text-slate-500">
                          Enviado em {editContract.contrato_arquivo_enviado_em ? new Date(editContract.contrato_arquivo_enviado_em).toLocaleString("pt-BR") : "-"}
                          {editContract.contrato_arquivo_enviado_por_nome ? ` por ${editContract.contrato_arquivo_enviado_por_nome}` : ""}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button type="button" size="sm" variant="outline" onClick={dlContract} data-testid="empresa-contrato-baixar"><Download size={14} /> Baixar</Button>
                        <label className="inline-flex items-center gap-2 text-sm border border-dashed border-slate-300 rounded-md px-3 py-1.5 cursor-pointer hover:bg-slate-50">
                          <Upload size={14} /> {contractUploading ? "Enviando..." : "Substituir"}
                          <input type="file" accept="application/pdf" className="hidden" data-testid="empresa-contrato-arquivo"
                            onChange={e => onContractFile(e.target.files?.[0])} disabled={contractUploading} />
                        </label>
                      </div>
                    </div>
                  ) : (
                    <label className="flex items-center gap-2 text-sm border border-dashed border-slate-300 rounded-md px-3 py-2 cursor-pointer hover:bg-slate-50 w-fit">
                      <Upload size={16} /> {contractUploading ? "Enviando..." : "Enviar contrato assinado (PDF)"}
                      <input type="file" accept="application/pdf" className="hidden" data-testid="empresa-contrato-arquivo"
                        onChange={e => onContractFile(e.target.files?.[0])} disabled={contractUploading} />
                    </label>
                  )}
                </div>
              </section>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={submit} data-testid="empresa-form-salvar">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Planos/Recursos (item 7) — card/popover "Plano atual + Recursos
          disponíveis" a partir da listagem, sem precisar abrir o formulário
          de edição completo. Somente leitura aqui. */}
      <Dialog open={!!resourcesFor} onOpenChange={v => { if (!v) { setResourcesFor(null); setResourcesData(null); } }}>
        <DialogContent data-testid="empresa-recursos-dialog">
          <DialogHeader><DialogTitle>Plano e recursos — {resourcesFor?.razao_social}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="text-xs text-slate-500">Plano atual</div>
              <div className="font-heading text-lg font-semibold">{resourcesData?.plan?.nome || "-"}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-2">Recursos disponíveis</div>
              <ul className="space-y-1.5">
                {(resourcesData?.resources || []).map(r => (
                  <li key={r.codigo} className="flex items-center gap-2 text-sm">
                    {r.available ? <Check size={15} className="text-emerald-600 shrink-0" /> : <X size={15} className="text-slate-300 shrink-0" />}
                    <span className={r.available ? "text-slate-900" : "text-slate-400 line-through"}>{r.nome}</span>
                    {r.available && r.origin === "override_enabled" && (
                      <span className="text-xs text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">Adicional contratado</span>
                    )}
                    {!r.available && r.origin === "override_disabled" && (
                      <span className="text-xs text-slate-400">Recurso não disponível para esta empresa</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Visualizar (somente dados cadastrais/administrativos — não é acesso operacional ao tenant) */}
      <Dialog open={!!viewing} onOpenChange={v => !v && setViewing(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{viewing?.razao_social}</DialogTitle></DialogHeader>
          {viewing && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><div className="text-xs text-slate-500">Nome Fantasia</div><div className="font-medium">{viewing.nome_fantasia || "-"}</div></div>
                <div><div className="text-xs text-slate-500">CPF/CNPJ</div><div className="font-medium">{viewing.cnpj || "-"}</div></div>
                <div><div className="text-xs text-slate-500">Telefone</div><div className="font-medium">{viewing.telefone || "-"}</div></div>
                <div><div className="text-xs text-slate-500">E-mail</div><div className="font-medium">{viewing.email || "-"}</div></div>
                <div><div className="text-xs text-slate-500">Situação</div><Badge className={viewing.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}>{viewing.status === "active" ? "Ativa" : "Inativa"}</Badge></div>
                <div><div className="text-xs text-slate-500">Cadastrada em</div><div className="font-medium">{fmtDate(viewing.created_at)}</div></div>
              </div>

              <div className="pt-3 border-t border-slate-100">
                <div className="text-xs text-slate-500 mb-1">Endereço</div>
                {(viewing.logradouro || viewing.municipio || viewing.cep) ? (
                  <div className="font-medium">{viewing.cep ? `CEP ${viewing.cep} — ` : ""}{enderecoLinha(viewing) || "-"}</div>
                ) : viewing.endereco ? (
                  <div>
                    <div className="font-medium">{viewing.endereco}</div>
                    <div className="text-xs text-slate-400">Endereço legado — cadastre nos campos estruturados na próxima edição.</div>
                  </div>
                ) : <div className="font-medium">-</div>}
              </div>

              <div className="pt-3 border-t border-slate-100">
                <div className="text-xs text-slate-500 mb-1">Responsável</div>
                <div className="font-medium">{viewing.responsavel_nome || "-"}</div>
                <div className="text-slate-500">{viewing.responsavel_email || "-"}</div>
              </div>

              {!!(viewing.contacts && viewing.contacts.length) && (
                <div className="pt-3 border-t border-slate-100">
                  <div className="text-xs text-slate-500 mb-1">Contatos</div>
                  {viewing.contacts.map((c, i) => <div key={i} className="font-medium">{c}</div>)}
                </div>
              )}

              {(viewing.situacao_cadastral || viewing.natureza_juridica || viewing.cnae_principal_codigo) && (
                <div className="pt-3 border-t border-slate-100 grid grid-cols-2 gap-3">
                  <div><div className="text-xs text-slate-500">Situação cadastral</div><div className="font-medium">{viewing.situacao_cadastral || "-"}</div></div>
                  <div><div className="text-xs text-slate-500">Natureza jurídica</div><div className="font-medium">{viewing.natureza_juridica || "-"}</div></div>
                  <div className="col-span-2"><div className="text-xs text-slate-500">CNAE principal</div><div className="font-medium">{viewing.cnae_principal_codigo ? `${viewing.cnae_principal_codigo} — ${viewing.cnae_principal_descricao || ""}` : "-"}</div></div>
                </div>
              )}

              {!!(viewing.cnaes_secundarios && viewing.cnaes_secundarios.length) && (
                <div className="pt-3 border-t border-slate-100">
                  <div className="text-xs text-slate-500 mb-1">Atividades secundárias</div>
                  {viewing.cnaes_secundarios.map((s, i) => (
                    <div key={i} className="font-medium">{s.codigo} — {s.descricao}</div>
                  ))}
                </div>
              )}

              <div className="pt-3 border-t border-slate-100 grid grid-cols-3 gap-3">
                <div><div className="text-xs text-slate-500">Plano LOOTUS</div><div className="font-medium">{editCommercial?.plano?.nome || "Sem plano"}</div></div>
                <div><div className="text-xs text-slate-500">Adesão</div><div className="font-medium">{viewing.data_adesao ? fmtDate(viewing.data_adesao) : "-"}</div></div>
                <div data-testid="empresa-ver-modelo-operacao">
                  <div className="text-xs text-slate-500">Modelo de operação</div>
                  <div className="font-medium">{modeloMeta(modeloOperacaoDe(viewing)).label}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Vencimento mensal</div>
                  <div className="font-medium">{editCommercial?.vencimento_mensal_dia ? `Todo dia ${editCommercial.vencimento_mensal_dia}` : "-"}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Próxima emissão</div>
                  <div className="font-medium">{editCommercial?.term?.cobranca_ativa && editCommercial?.proxima_emissao ? fmtDate(editCommercial.proxima_emissao) : "-"}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Próximo vencimento</div>
                  <div className="font-medium">{editCommercial?.term?.cobranca_ativa && editCommercial?.proximo_vencimento ? fmtDate(editCommercial.proximo_vencimento) : "-"}</div>
                </div>
              </div>

              <div className="pt-3 border-t border-slate-100">
                <div className="text-xs text-slate-500 mb-1">Administrador principal</div>
                <div className="font-medium">{viewing.primary_admin?.name || "-"}</div>
              </div>

              {/* Pacote prioritário — E-MAIL DE ACESSO (login/recuperação de
                  senha do administrador principal) é DIFERENTE do e-mail
                  comercial/financeiro logo abaixo — nunca o mesmo campo,
                  nunca a mesma tela de edição. */}
              <div className="pt-3 border-t border-slate-100 flex items-start justify-between gap-2">
                <div>
                  <div className="text-xs text-slate-500 mb-1 flex items-center gap-1"><Mail size={12} /> E-mail de acesso ao LOOTUS</div>
                  <div className="font-medium">{viewing.primary_admin?.email || "-"}</div>
                  <div className="text-[11px] text-slate-400">Utilizado pelo administrador principal para entrar no sistema e recuperar a senha.</div>
                </div>
                {viewing.primary_admin?.id ? (
                  <Button size="sm" variant="outline" onClick={() => { setAccessEmailCompany(viewing); setAccessEmailForm(emptyAccessEmail); }} data-testid="empresa-editar-email-acesso">
                    <Pencil size={14} /> Alterar
                  </Button>
                ) : (
                  <span className="text-[11px] text-amber-600 max-w-[160px] text-right">Empresa sem administrador principal definido</span>
                )}
              </div>

              <div className="pt-3 border-t border-slate-100">
                <div className="text-xs text-slate-500 mb-1">E-mail comercial/financeiro</div>
                <div className="text-slate-500">{viewing.primary_email || "-"}</div>
                <div className="text-[11px] text-slate-400">Usado para cobrança, boleto e avisos financeiros — nunca para login.</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Alterar e-mail de acesso do administrador principal (Pacote
          prioritário) — nunca confundir com o e-mail comercial acima. */}
      <Dialog open={!!accessEmailCompany} onOpenChange={v => !v && setAccessEmailCompany(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Alterar e-mail de acesso — {accessEmailCompany?.primary_admin?.name || accessEmailCompany?.razao_social}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              Este é o e-mail que o administrador principal usa para entrar no LOOTUS e recuperar a senha.
              Não altera o e-mail comercial/financeiro da empresa nem afeta outros usuários.
            </p>
            <div>
              <Label>Novo e-mail de acesso</Label>
              <Input type="email" data-testid="empresa-access-email-novo" value={accessEmailForm.new_email}
                onChange={e => setAccessEmailForm({ new_email: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAccessEmailCompany(null)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={submitAccessEmail} data-testid="empresa-access-email-salvar">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Definir administrador principal (item 20) — só aparece quando a
          empresa realmente não tem nenhum; reaproveita a mesma validação
          de nome/e-mail/senha já usada no cadastro de empresa nova. */}
      <Dialog open={!!primaryAdminCompany} onOpenChange={v => !v && setPrimaryAdminCompany(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Definir administrador principal — {primaryAdminCompany?.razao_social}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">
              Este usuário será o administrador principal da empresa — usa o e-mail informado para entrar no LOOTUS e recuperar a senha.
            </p>
            <div><Label>Nome</Label><Input data-testid="primary-admin-nome" value={primaryAdminForm.name} onChange={e => setPrimaryAdminForm({ ...primaryAdminForm, name: e.target.value })} /></div>
            <div><Label>E-mail de acesso</Label><Input type="email" data-testid="primary-admin-email" value={primaryAdminForm.email} onChange={e => setPrimaryAdminForm({ ...primaryAdminForm, email: e.target.value })} /></div>
            <div>
              <Label>Senha provisória</Label>
              <Input type="password" data-testid="primary-admin-senha" value={primaryAdminForm.password} onChange={e => setPrimaryAdminForm({ ...primaryAdminForm, password: e.target.value })} />
              <p className="text-xs text-slate-400 mt-1">{PASSWORD_HELP_TEXT}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPrimaryAdminCompany(null)}>Cancelar</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={submitCreatePrimaryAdmin} data-testid="primary-admin-salvar">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Redefinir senha do administrador principal (2FA — Pacote 6) */}
      <AdminResetClientPasswordDialog company={pwCompany} open={!!pwCompany} onOpenChange={v => !v && setPwCompany(null)} />

      {/* Contrato Permanente + Régua de Inadimplência (item 9/17) —
          histórico de notificações. Só leitura; nenhuma ação de reenvio
          manual nesta tela (fora do escopo pedido). */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><History size={18} /> Histórico de notificações</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {historyLoading && <div className="text-center py-6 text-slate-400 text-sm">Carregando...</div>}
            {!historyLoading && historyItems.length === 0 && (
              <div className="text-center py-6 text-slate-400 text-sm">Nenhuma notificação registrada ainda.</div>
            )}
            {!historyLoading && historyItems.map(n => (
              <div key={n.id} className="p-3 bg-slate-50 border border-slate-200 rounded-md flex items-start justify-between gap-3" data-testid={`historico-notificacao-${n.id}`}>
                <div>
                  <div className="text-sm font-medium text-slate-900 flex items-center gap-2">
                    {n.resultado === "enviado" ? <Check size={14} className="text-emerald-600" /> : <ShieldAlert size={14} className="text-amber-600" />}
                    {OVERDUE_TIPO_LABEL[n.tipo] || n.tipo}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Competência {n.competencia} · {n.dias_atraso} dia(s) de atraso
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <Badge className={n.resultado === "enviado" ? "bg-emerald-100 text-emerald-800" : n.resultado === "falha" ? "bg-red-100 text-red-800" : "bg-slate-200 text-slate-700"}>
                    {OVERDUE_RESULTADO_LABEL[n.resultado] || n.resultado}
                  </Badge>
                  <div className="text-xs text-slate-400 mt-1">{n.sent_at ? new Date(n.sent_at).toLocaleString("pt-BR") : new Date(n.created_at).toLocaleString("pt-BR")}</div>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setHistoryOpen(false)}>Fechar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
